import {
  lazy,
  Suspense,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ClientOnly, getRouteApi, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  Crosshair,
  Group,
  ListFilter,
  Locate,
  Minus,
  Palette,
  Plus,
  RotateCcw,
  SlidersHorizontal,
  Ungroup,
  X,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import type { Purpose } from "@/lib/mock-data";
import { PURPOSES } from "@/lib/mock-data";
import { ListingBottomCard } from "./ListingBottomCard";
import { MapSearchBar } from "./MapSearchBar";
import {
  DEFAULT_MAP_FILTERS,
  applyMapFilters,
  countActiveFilters,
  type MapFilters,
} from "@/lib/map-filters";
import { MapLegend } from "./MapLegend";
import { MapErrorBoundary } from "./MapErrorBoundary";
import {
  DEFAULT_RADIUS_KM,
  nearbyListings,
  useGeolocation,
  withDistance,
  type NearbySort,
} from "@/lib/geo";
import {
  initGpsDebug,
  isGpsDebugEnabled,
  gpsLog,
  setGpsDebug,
  subscribeGpsLog,
  type GpsLogEntry,
} from "@/lib/gps-debug";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { fetchPublishedProperties, projectToViewport } from "@/lib/properties";
import { fetchPublishedParking, parkingOrDemo } from "@/lib/parking";
import { generateStressDataset } from "@/lib/stress-data";
import { matchesQuery, parkingToItem, propertyToItem, type MapItem } from "@/lib/map-search";
import { isPerfEnabled, recordViewportCommit, setPerfEnabled } from "@/lib/perf";
import { SaveSearchDialog } from "./SaveSearchDialog";
import type { FlyTarget, MapViewport } from "./GoogleMapCanvas";

/** Mirrors google-maps.ts — kept local so SSR never pulls in the Maps loader. */
const HAS_CUSTOM_MAP_ID = Boolean(import.meta.env.VITE_GOOGLE_MAP_ID);

/** Google Maps is browser-only — loaded after hydration. */
const GoogleMapCanvas = lazy(() =>
  import("./GoogleMapCanvas").then((m) => ({ default: m.GoogleMapCanvas })),
);

/**
 * The side panels are not part of the first interactive frame — they are only
 * mounted once the map is interactive (or when the user opens them), so initial
 * render work stays with the map itself.
 */
const MapFilterDrawer = lazy(() =>
  import("./MapFilterDrawer").then((m) => ({ default: m.MapFilterDrawer })),
);
const NearbyPanel = lazy(() => import("./NearbyPanel").then((m) => ({ default: m.NearbyPanel })));
const PropertyDetailsPanel = lazy(() =>
  import("./PropertyDetailsPanel").then((m) => ({ default: m.PropertyDetailsPanel })),
);
const ParkingDetailsPanel = lazy(() =>
  import("./ParkingDetailsPanel").then((m) => ({ default: m.ParkingDetailsPanel })),
);
const MapPerfPanel = lazy(() =>
  import("./MapPerfPanel").then((m) => ({ default: m.MapPerfPanel })),
);

/** Skeleton shown while a lazy side panel chunk arrives. */
function PanelFallback() {
  return (
    <div className="space-y-3 p-4">
      <div className="h-4 w-1/3 animate-pulse rounded bg-secondary" />
      <div className="h-24 animate-pulse rounded-xl bg-secondary" />
      <div className="h-24 animate-pulse rounded-xl bg-secondary" />
    </div>
  );
}

const MIN_ZOOM = 4;
const MAX_ZOOM = 20;
const DEFAULT_ZOOM = 12;
/** Default view: Madurai, Tamil Nadu (kept in sync with GoogleMapCanvas). */
const DEFAULT_CENTER = { lat: 9.9252, lng: 78.1198 };

const homeRoute = getRouteApi("/");

const round5 = (n: number) => Math.round(n * 1e5) / 1e5;

/** Shape of the persisted map viewport in the URL (mirrors the route schema). */
interface MapSearch {
  lat?: number;
  lng?: number;
  z?: number;
  cluster?: false;
  stress?: number;
  sel?: string;
}

export function MapView() {
  const search = homeRoute.useSearch();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<MapFilters>(DEFAULT_MAP_FILTERS);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(search.sel ?? null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [saveSearchOpen, setSaveSearchOpen] = useState(false);
  const [fullDetails, setFullDetails] = useState(false);
  const [nearbyOpen, setNearbyOpen] = useState(false);
  const [radiusKm, setRadiusKm] = useState<number>(DEFAULT_RADIUS_KM);
  const [sort, setSort] = useState<NearbySort>("nearest");
  const [tilesLoading, setTilesLoading] = useState(true);
  const [geoDismissed, setGeoDismissed] = useState(false);
  /** Quick A/B of the custom (styled) Map ID vs Google's default styling. */
  const [styledMap, setStyledMap] = useState(true);
  /** True once the map has painted — gates the lazy side-panel chunks. */
  const [interactive, setInteractive] = useState(false);
  const [perfOpen, setPerfOpen] = useState(false);

  /** Viewport persisted in the URL so a refresh restores the exact view. */
  const initialView = useRef<MapViewport>({
    lat: search.lat ?? DEFAULT_CENTER.lat,
    lng: search.lng ?? DEFAULT_CENTER.lng,
    zoom: search.z ?? DEFAULT_ZOOM,
  }).current;
  const [view, setView] = useState<MapViewport>(initialView);
  const [flyTarget, setFlyTarget] = useState<FlyTarget | null>(null);
  const clustering = search.cluster !== false;
  const stressCount = search.stress ?? 0;
  const zoom = view.zoom;

  useEffect(() => {
    setPerfOpen(isPerfEnabled());
  }, []);

  /** Viewport commits arrive already throttled from the canvas. */
  const handleViewportChange = useCallback((next: MapViewport) => {
    recordViewportCommit();
    setView(next);
  }, []);

  const handleTilesLoadingChange = useCallback((loading: boolean) => {
    setTilesLoading(loading);
    if (!loading) setInteractive(true);
  }, []);

  /**
   * Safety net: even if Google never reports tiles (offline, blocked key), the
   * panels must still become available shortly after hydration.
   */
  useEffect(() => {
    if (interactive) return;
    const t = window.setTimeout(() => setInteractive(true), 2500);
    return () => window.clearTimeout(t);
  }, [interactive]);

  const {
    origin,
    setCity,
    locate,
    status: geoStatus,
    error: geoError,
    fix: geoFix,
    clearError,
  } = useGeolocation();

  /** Push viewport changes into the URL (debounced, replace — no history spam). */
  useEffect(() => {
    const t = window.setTimeout(() => {
      void navigate({
        to: "/",
        search: (prev: MapSearch) => ({
          ...prev,
          lat: round5(view.lat),
          lng: round5(view.lng),
          z: Math.round(view.zoom),
        }),
        replace: true,
        resetScroll: false,
      });
    }, 400);
    return () => window.clearTimeout(t);
  }, [view.lat, view.lng, view.zoom, navigate]);

  /**
   * Keep the selected listing in the URL so a refresh reopens the same pin —
   * debounced so rapid pin-hopping doesn't trigger a router update per tap.
   */
  useEffect(() => {
    const t = window.setTimeout(() => {
      void navigate({
        to: "/",
        search: (prev: MapSearch) => ({ ...prev, sel: selectedId ?? undefined }),
        replace: true,
        resetScroll: false,
      });
    }, 250);
    return () => window.clearTimeout(t);
  }, [selectedId, navigate]);

  const handleSelect = useCallback((id: string) => {
    setSelectedId(id);
    setFullDetails(false);
  }, []);

  const flyTo = useCallback((lat: number, lng: number, nextZoom: number, reason = "manual") => {
    const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, nextZoom));
    gpsLog({
      kind: "decision",
      message: `centering map (${reason})`,
      lat,
      lng,
      detail: { zoom: clamped, requestedZoom: nextZoom },
    });
    setView({ lat, lng, zoom: clamped });
    setFlyTarget({ lat, lng, zoom: clamped, token: Date.now() });
  }, []);

  /**
   * Robust GPS fallback: Madurai is only a placeholder. On first load (when the
   * URL carries no explicit viewport and no city origin is stored) we ask the
   * device for a fix and recentre as soon as a valid one arrives. If the device
   * never answers, or permission is denied, the Madurai default simply stays.
   */
  const autoCentered = useRef(false);
  const hasUrlView = search.lat != null && search.lng != null;
  useEffect(() => {
    if (autoCentered.current || hasUrlView) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    let cancelled = false;

    const start = async () => {
      // Never prompt again once the user has denied location for this origin.
      try {
        const perm = await navigator.permissions?.query({ name: "geolocation" as PermissionName });
        if (perm && perm.state === "denied") return;
      } catch {
        /* Permissions API unavailable — fall through and let locate() ask. */
      }
      if (cancelled || autoCentered.current) return;
      const next = await locate();
      if (cancelled || !next || autoCentered.current) return;
      autoCentered.current = true;
      flyTo(next.lat, next.lng, 16, "auto-centre on first valid device fix");
    };

    void start();
    return () => {
      cancelled = true;
    };
    // First load only — later fixes are handled by the Locate Me control.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setClustering = (next: boolean) => {
    void navigate({
      to: "/",
      search: (prev: MapSearch) => ({ ...prev, cluster: next ? undefined : (false as const) }),
      replace: true,
      resetScroll: false,
    });
  };

  const { data: propertyRows = [], isLoading } = useQuery({
    queryKey: ["properties", "published"],
    queryFn: fetchPublishedProperties,
  });
  const { data: parkingRows = [], isLoading: parkingLoading } = useQuery({
    queryKey: ["parking", "published"],
    queryFn: fetchPublishedParking,
  });

  /** Unified, approved-only feed. */
  const allItems = useMemo<MapItem[]>(() => {
    const properties = projectToViewport(propertyRows)
      .filter((p) => p.purpose !== "parking")
      .map(propertyToItem);
    const parking = parkingOrDemo(parkingRows).map(parkingToItem);
    // Optional synthetic load for performance testing (`/?stress=2000`).
    const stress = stressCount
      ? (() => {
          const set = generateStressDataset(stressCount);
          return [...set.properties.map(propertyToItem), ...set.parking.map(parkingToItem)];
        })()
      : [];
    return withDistance([...properties, ...parking, ...stress], origin);
  }, [propertyRows, parkingRows, origin, stressCount]);

  const results = useMemo(
    () => applyMapFilters(allItems, filters).filter((i) => matchesQuery(i, query)),
    [allItems, filters, query],
  );

  /** Radius-filtered + sorted feed shown in the Nearby Listings panel. */
  const nearby = useMemo(
    () => nearbyListings(results, origin, radiusKm, sort),
    [results, origin, radiusKm, sort],
  );
  /**
   * Side panels render from deferred values, so a burst of pin taps or a pan
   * never blocks the map thread on list/details re-rendering.
   */
  const nearbyDeferred = useDeferredValue(nearby);
  const deferredSelectedId = useDeferredValue(selectedId);

  const selected = results.find((i) => i.id === deferredSelectedId) ?? null;

  const parkingMode = filters.purpose === "parking";
  const loading = isLoading || parkingLoading;

  // Drop a selection (including a stale one restored from the URL) only once
  // the listings have actually loaded.
  useEffect(() => {
    if (loading) return;
    if (selectedId && !results.some((i) => i.id === selectedId)) setSelectedId(null);
  }, [results, selectedId, loading]);

  const busy = loading || tilesLoading;
  const activeFilterCount = countActiveFilters(filters) + (query ? 1 : 0);

  const clearAll = () => {
    setFilters(DEFAULT_MAP_FILTERS);
    setQuery("");
    setSelectedId(null);
  };

  const setPurpose = (p: Purpose | "all") => {
    setSelectedId(null);
    setFilters((f) => ({
      ...f,
      purpose: p,
      propertyType: p === "parking" ? "all" : f.propertyType,
      amenities: [],
    }));
  };

  const locateMe = async () => {
    setGeoDismissed(false);
    const next = await locate();
    if (next) {
      // Centre on the exact device coordinates.
      flyTo(next.lat, next.lng, 18, `locate-me · accuracy ${Math.round(next.accuracy ?? 0)} m`);
      setNearbyOpen(true);
      toast.success("Map centred on your current location.");
    } else {
      setNearbyOpen(true);
    }
  };

  const pickCity = (city: { name: string; lat: number; lng: number }) => {
    setCity(city);
    setGeoDismissed(true);
    flyTo(city.lat, city.lng, Math.max(zoom, DEFAULT_ZOOM));
  };

  const recenter = () => {
    setSelectedId(null);
    flyTo(origin?.lat ?? DEFAULT_CENTER.lat, origin?.lng ?? DEFAULT_CENTER.lng, DEFAULT_ZOOM);
    toast.success(origin ? `Map recentred on ${origin.label}.` : "Map recentred.");
  };

  /** Back to the default Madurai view (URL params follow automatically). */
  const resetView = () => {
    setSelectedId(null);
    flyTo(DEFAULT_CENTER.lat, DEFAULT_CENTER.lng, DEFAULT_ZOOM);
    toast.success("Map reset to the default Madurai view.");
  };

  const dismissGeoMessage = () => {
    setGeoDismissed(true);
    clearError();
  };

  const showGeoMessage = Boolean(geoError) && !geoDismissed;
  const improvingGps = geoStatus === "improving";
  const [gpsDebug, setGpsDebugOn] = useState(false);
  const [gpsEntries, setGpsEntries] = useState<GpsLogEntry[]>([]);

  // Real-device debug mode: ?gpsdebug=1, sessionStorage, or window.landooGps.enable()
  useEffect(() => {
    initGpsDebug();
    setGpsDebugOn(isGpsDebugEnabled());
    const unsubscribe = subscribeGpsLog((e: GpsLogEntry[]) => {
      setGpsEntries(e);
      setGpsDebugOn(isGpsDebugEnabled());
    });
    return unsubscribe;
  }, []);

  const showDiagnostics = import.meta.env.DEV || gpsDebug;

  const detailsPanel = selected ? (
    <Suspense fallback={<PanelFallback />}>
      {selected.kind === "parking" && selected.parking ? (
        <ParkingDetailsPanel parking={selected.parking} onClose={() => setSelectedId(null)} />
      ) : selected.property ? (
        <PropertyDetailsPanel property={selected.property} onClose={() => setSelectedId(null)} />
      ) : null}
    </Suspense>
  ) : null;

  return (
    <div className="relative flex h-[calc(100dvh-62px-76px)] w-full md:h-[calc(100dvh-62px)]">
      <div className="relative flex-1">
        {/* ---------- Google Maps canvas ---------- */}
        <div className="landoo-map-bg absolute inset-0 z-0">
          <ClientOnly fallback={null}>
            <MapErrorBoundary>
              <Suspense fallback={null}>
                <GoogleMapCanvas
                  key={styledMap ? "styled" : "default"}
                  items={results}
                  origin={origin}
                  selectedId={selectedId}
                  initialView={view}
                  flyTarget={flyTarget}
                  zoom={zoom}
                  styled={styledMap}
                  clustering={clustering}
                  onViewportChange={handleViewportChange}
                  onTilesLoadingChange={handleTilesLoadingChange}
                  onSelect={handleSelect}
                />
              </Suspense>
            </MapErrorBoundary>
          </ClientOnly>
        </div>

        {/* ---------- Lightweight loading pill (never blocks the UI) ---------- */}
        {busy && (
          <div className="pointer-events-none absolute left-1/2 top-32 z-20 -translate-x-1/2">
            <div className="flex items-center gap-2 rounded-full bg-background/95 px-3 py-1.5 text-[11px] font-semibold text-brown-dark shadow-float backdrop-blur">
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-brand border-t-transparent" />
              {loading ? "Loading listings…" : "Loading map…"}
            </div>
          </div>
        )}

        {/* ---------- GPS accuracy notice ---------- */}
        {improvingGps && !showGeoMessage && (
          <div className="pointer-events-none absolute left-1/2 top-44 z-20 -translate-x-1/2">
            <div className="flex items-center gap-2 rounded-full bg-background/95 px-3 py-1.5 text-[11px] font-semibold text-brown-dark shadow-float backdrop-blur">
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-brand border-t-transparent" />
              Improving GPS accuracy…
            </div>
          </div>
        )}

        {/* ---------- Dev-only GPS diagnostics ---------- */}
        {showDiagnostics && (
          <div className="absolute left-3 top-32 z-30 max-w-[16rem] rounded-lg bg-brown-dark/90 px-2.5 py-2 font-mono text-[10px] leading-relaxed text-white shadow-float">
            <div className="flex items-center justify-between gap-2">
              <span className="font-bold">GPS diagnostics</span>
              <button
                type="button"
                className="rounded bg-white/15 px-1.5 py-0.5 text-[9px] font-bold uppercase"
                onClick={() => setGpsDebug(!gpsDebug)}
              >
                {gpsDebug ? "debug on" : "debug off"}
              </button>
            </div>
            <div>lat: {geoFix ? geoFix.lat : "—"}</div>
            <div>lng: {geoFix ? geoFix.lng : "—"}</div>
            <div>accuracy: {geoFix ? `${Math.round(geoFix.accuracy)} m` : "—"}</div>
            <div>time: {geoFix ? new Date(geoFix.timestamp).toLocaleTimeString() : "—"}</div>
            <div>status: {geoStatus}</div>
            <div>error: {geoError ?? "none"}</div>
            {gpsDebug && (
              <div className="mt-1.5 max-h-40 overflow-auto border-t border-white/20 pt-1.5">
                {gpsEntries.length === 0 ? (
                  <div className="opacity-70">No events yet — tap Locate me.</div>
                ) : (
                  gpsEntries.slice(0, 20).map((e, i) => (
                    <div key={`${e.t}-${i}`} className="opacity-90">
                      <span className="opacity-60">
                        {new Date(e.t).toLocaleTimeString()} [{e.kind}]
                      </span>{" "}
                      {e.message}
                      {e.lat != null && (
                        <div className="pl-2 opacity-75">
                          {e.lat}, {e.lng} ±{e.accuracy != null ? Math.round(e.accuracy) : "?"}m
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* ---------- Location error message ---------- */}
        {showGeoMessage && (
          <div className="absolute inset-x-3 bottom-24 z-20 mx-auto max-w-sm md:bottom-16">
            <div className="flex items-start gap-2.5 rounded-2xl bg-background/97 p-3 shadow-float backdrop-blur">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-coral" />
              <div className="flex-1">
                <div className="text-xs font-bold text-brown-dark">
                  {geoStatus === "denied"
                    ? "Location permission denied"
                    : geoStatus === "timeout"
                      ? "Locating timed out"
                      : "Location unavailable"}
                </div>
                <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{geoError}</p>
                <div className="mt-2 flex gap-2">
                  {geoStatus !== "denied" && (
                    <button
                      onClick={locateMe}
                      className="rounded-lg bg-brand px-2.5 py-1 text-[11px] font-semibold text-brown-dark"
                    >
                      Retry Location
                    </button>
                  )}
                  <button
                    onClick={() => {
                      dismissGeoMessage();
                      setNearbyOpen(true);
                    }}
                    className="rounded-lg bg-secondary px-2.5 py-1 text-[11px] font-semibold text-brown-dark"
                  >
                    Search a city
                  </button>
                </div>
              </div>
              <button
                onClick={dismissGeoMessage}
                aria-label="Dismiss location message"
                className="text-muted-foreground hover:text-brown-dark"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* ---------- Search + purpose row ---------- */}
        <div className="absolute inset-x-3 top-3 z-20 space-y-2">
          <div className="flex items-center gap-2">
            <div className="max-w-xl flex-1">
              <MapSearchBar
                value={query}
                onChange={setQuery}
                items={allItems}
                onPickPlace={(place) => {
                  setSelectedId(null);
                  flyTo(place.lat, place.lng, Math.max(zoom, 14));
                }}
              />
            </div>
            <button
              type="button"
              onClick={() => setFiltersOpen(true)}
              aria-label="Open map filters"
              className={`relative flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2.5 text-xs font-semibold shadow-float transition ${
                parkingMode ? "bg-parking text-white" : "bg-background text-brown-dark"
              }`}
            >
              <SlidersHorizontal className="h-4 w-4" />
              <span className="hidden sm:inline">Filters</span>
              {activeFilterCount > 0 && (
                <span className="ml-0.5 rounded-full bg-coral px-1.5 text-[10px] font-bold text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
          <PurposeSelector value={filters.purpose} onChange={setPurpose} floating />
        </div>

        {/* ---------- No results ---------- */}
        {!loading && results.length === 0 && (
          <div className="absolute left-1/2 top-1/2 w-[86%] max-w-xs -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-background/95 p-5 text-center shadow-float backdrop-blur">
            <div className="text-sm font-bold text-brown-dark">
              {filters.purpose === "all"
                ? "No listings here yet."
                : `No ${filters.purpose} listings here yet.`}
            </div>

            <p className="mt-1 text-xs text-muted-foreground">
              {activeFilterCount > 0
                ? "Try a different city, area or pincode, or widen your filters."
                : "Move the map or zoom out to see listings in another area."}
            </p>
            <button
              onClick={clearAll}
              className="mt-3 w-full rounded-xl bg-brand py-2.5 text-sm font-semibold text-brown-dark shadow-soft transition hover:brightness-105"
            >
              {activeFilterCount > 0 ? "Clear Filters" : "Show all listings"}
            </button>
          </div>
        )}

        {/* ---------- Map controls ---------- */}
        <div className="absolute right-3 top-28 z-10 flex flex-col gap-2 md:top-28">
          <button
            onClick={() => setClustering(!clustering)}
            aria-label={clustering ? "Disable marker clustering" : "Enable marker clustering"}
            aria-pressed={clustering}
            title={clustering ? "Clustering on — show every pin" : "Clustering off — group pins"}
            className={`flex h-9 w-9 items-center justify-center rounded-full shadow-float transition ${
              clustering
                ? "bg-brown text-primary-foreground"
                : "bg-background text-brown-dark hover:bg-brand-soft"
            }`}
          >
            {clustering ? <Group className="h-4 w-4" /> : <Ungroup className="h-4 w-4" />}
          </button>

          {HAS_CUSTOM_MAP_ID && (
            <button
              onClick={() => setStyledMap(!styledMap)}
              aria-label={styledMap ? "Preview default map styling" : "Use custom map styling"}
              aria-pressed={styledMap}
              title={
                styledMap
                  ? "Custom Map ID styling — tap for default"
                  : "Default styling — tap for custom"
              }
              className={`flex h-9 w-9 items-center justify-center rounded-full shadow-float transition ${
                styledMap
                  ? "bg-brown text-primary-foreground"
                  : "bg-background text-brown-dark hover:bg-brand-soft"
              }`}
            >
              <Palette className="h-4 w-4" />
            </button>
          )}

          <button
            onClick={locateMe}
            aria-label="Locate me"
            title="Locate me"
            className={`flex h-9 w-9 items-center justify-center rounded-full shadow-float transition ${
              origin?.source === "gps"
                ? "bg-rent text-white"
                : "bg-background text-brown-dark hover:bg-brand-soft"
            }`}
          >
            <Locate className={`h-4 w-4 ${geoStatus === "locating" ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={recenter}
            aria-label="Recenter map"
            title="Recenter map"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-background text-brown-dark shadow-float transition hover:bg-brand-soft"
          >
            <Crosshair className="h-4 w-4" />
          </button>
          <button
            onClick={resetView}
            aria-label="Reset map view"
            title="Reset to default view (Madurai)"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-background text-brown-dark shadow-float transition hover:bg-brand-soft"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <button
            onClick={() => setNearbyOpen(true)}
            aria-label="Nearby listings"
            title="Nearby listings"
            className="relative flex h-9 w-9 items-center justify-center rounded-full bg-background text-brown-dark shadow-float transition hover:bg-brand-soft lg:hidden"
          >
            <ListFilter className="h-4 w-4" />
            {nearby.length > 0 && (
              <span className="absolute -right-1 -top-1 rounded-full bg-coral px-1.5 text-[10px] font-bold text-white">
                {nearby.length}
              </span>
            )}
          </button>
        </div>

        <div className="absolute bottom-3 right-3 z-10 flex flex-col overflow-hidden rounded-full bg-background shadow-float">
          <button
            aria-label="Zoom in"
            onClick={() => flyTo(view.lat, view.lng, zoom + 1)}
            className="flex h-9 w-9 items-center justify-center text-brown-dark hover:bg-brand-soft"
          >
            <Plus className="h-4 w-4" />
          </button>
          <div className="flex h-6 items-center justify-center text-[10px] font-bold text-muted-foreground">
            {zoom}x
          </div>
          <div className="h-px w-full bg-border" />
          <button
            aria-label="Zoom out"
            onClick={() => flyTo(view.lat, view.lng, zoom - 1)}
            className="flex h-9 w-9 items-center justify-center text-brown-dark hover:bg-brand-soft"
          >
            <Minus className="h-4 w-4" />
          </button>
        </div>

        <div
          className={`pointer-events-none absolute left-3 z-10 max-w-[62%] md:max-w-none ${
            selected ? "bottom-[13.5rem] md:bottom-3" : "bottom-16 md:bottom-3"
          }`}
        >
          <MapLegend active={filters.purpose} />
        </div>

        {/* ---------- Bottom sheet list card ---------- */}
        {selected && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 pb-14 md:pb-0 lg:hidden">
            <div className="pointer-events-auto mx-auto w-full max-w-lg overflow-hidden rounded-t-3xl border border-border bg-card shadow-float">
              <div className="mx-auto my-2 h-1.5 w-10 rounded-full bg-border" />
              {fullDetails ? (
                <div className="max-h-[62vh] overflow-y-auto">
                  <button
                    onClick={() => setFullDetails(false)}
                    className="flex w-full items-center gap-1 px-3 py-1 text-[11px] font-semibold text-muted-foreground"
                  >
                    <X className="h-3.5 w-3.5" /> Close details
                  </button>
                  {detailsPanel}
                </div>
              ) : (
                <ListingBottomCard
                  item={selected}
                  origin={origin}
                  onClose={() => setSelectedId(null)}
                  onViewDetails={() => setFullDetails(true)}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* ---------- Desktop details rail (mounted after the map is interactive) ---------- */}
      <aside className="hidden w-[380px] shrink-0 border-l border-border bg-background lg:block">
        {interactive ? (
          (detailsPanel ?? (
            <Suspense fallback={<PanelFallback />}>
              <NearbyPanel
                items={nearbyDeferred}
                origin={origin}
                status={geoStatus}
                error={geoError}
                radiusKm={radiusKm}
                sort={sort}
                onRadiusChange={setRadiusKm}
                onSortChange={setSort}
                onLocate={locateMe}
                onRecenter={recenter}
                onPickCity={pickCity}
                onSelect={(id) => {
                  setSelectedId(id);
                  setFullDetails(false);
                }}
              />
            </Suspense>
          ))
        ) : (
          <PanelFallback />
        )}
      </aside>

      {/* ---------- Nearby listings sheet (mobile / tablet) ---------- */}
      <Sheet open={nearbyOpen} onOpenChange={setNearbyOpen}>
        <SheetContent side="right" className="w-full p-0 sm:max-w-sm lg:hidden">
          <SheetTitle className="sr-only">Nearby listings</SheetTitle>
          <SheetDescription className="sr-only">
            Listings near your current location, sorted and filtered by distance.
          </SheetDescription>
          <Suspense fallback={<PanelFallback />}>
            <NearbyPanel
              items={nearbyDeferred}
              origin={origin}
              status={geoStatus}
              error={geoError}
              radiusKm={radiusKm}
              sort={sort}
              onRadiusChange={setRadiusKm}
              onSortChange={setSort}
              onLocate={locateMe}
              onRecenter={recenter}
              onPickCity={pickCity}
              onSelect={(id) => {
                setSelectedId(id);
                setFullDetails(false);
                setNearbyOpen(false);
              }}
              onClose={() => setNearbyOpen(false)}
            />
          </Suspense>
        </SheetContent>
      </Sheet>

      {/* ---------- Slide-in filter panel ---------- */}
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="right" className="w-full p-0 sm:max-w-sm">
          <SheetTitle className="sr-only">Map filters</SheetTitle>
          <SheetDescription className="sr-only">
            Refine map results by purpose, price, bedrooms and other filters.
          </SheetDescription>
          <Suspense fallback={<PanelFallback />}>
            <MapFilterDrawer
              filters={filters}
              onChange={(f) => {
                setSelectedId(null);
                setFilters(f);
              }}
              onReset={clearAll}
              onDone={() => setFiltersOpen(false)}
              onSaveSearch={() => setSaveSearchOpen(true)}
              resultCount={results.length}
            />
          </Suspense>
        </SheetContent>
      </Sheet>

      <SaveSearchDialog
        open={saveSearchOpen}
        onOpenChange={setSaveSearchOpen}
        filters={filters}
        context={{ query, origin }}
      />

      {/* ---------- In-app performance meter (?perf=1) ---------- */}
      {perfOpen && (
        <Suspense fallback={null}>
          <MapPerfPanel
            onClose={() => {
              setPerfEnabled(false);
              setPerfOpen(false);
            }}
          />
        </Suspense>
      )}
    </div>
  );
}

function PurposeSelector({
  value,
  onChange,
  floating,
}: {
  value: Purpose | "all";
  onChange: (p: Purpose | "all") => void;
  floating?: boolean;
}) {
  return (
    <div
      className={`flex max-w-md items-center gap-1 overflow-x-auto rounded-full p-1 ${
        floating ? "bg-background/95 shadow-float backdrop-blur" : "bg-secondary/60"
      }`}
    >
      {PURPOSES.map((p) => {
        const active = value === p.value;
        const parking = p.value === "parking";
        return (
          <button
            key={p.value}
            type="button"
            onClick={() => onChange(p.value)}
            className={`flex-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition ${
              active
                ? parking
                  ? "bg-parking text-white shadow-soft"
                  : "bg-brown text-primary-foreground shadow-soft"
                : "text-brown-dark hover:bg-brand-soft"
            }`}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}
